/**
 * HeatChip — graduated red→amber→teal→blue pill for lead/proposal heat.
 *
 * Input: { band: "hot"|"warm"|"cool"|"cold", days: number|null, color: "#..." }
 *
 * We tint the background by how deep into the band the item sits, so two
 * "warm" items 11 vs 21 days old look visually different.
 */
const BAND_CONFIG = {
  hot:  { label: "Hot",  min: 0,  max: 10, base: [220, 38, 38],  fg: "#FFFFFF" }, // #DC2626
  warm: { label: "Warm", min: 11, max: 21, base: [217, 119, 6],  fg: "#FFFFFF" }, // #D97706
  cool: { label: "Cool", min: 22, max: 60, base: [13, 148, 136], fg: "#FFFFFF" }, // #0D9488
  cold: { label: "Cold", min: 61, max: 90, base: [37, 99, 235],  fg: "#FFFFFF" }, // #2563EB
};

function tintFor(band, days) {
  const cfg = BAND_CONFIG[band] || BAND_CONFIG.cold;
  if (days == null) return `rgba(${cfg.base.join(",")},0.85)`;
  const span = Math.max(1, cfg.max - cfg.min);
  const pos = Math.min(1, Math.max(0, (days - cfg.min) / span));
  // Fade from full saturation (at band start) to 55% (at band end)
  const alpha = 1 - pos * 0.45;
  return `rgba(${cfg.base.join(",")},${alpha.toFixed(2)})`;
}

export default function HeatChip({ heat, showDays = true, size = "sm" }) {
  const band = heat?.band || "cold";
  const days = heat?.days;
  const cfg = BAND_CONFIG[band] || BAND_CONFIG.cold;
  const bg = tintFor(band, days);
  const padY = size === "xs" ? 1 : 3;
  const padX = size === "xs" ? 6 : 8;
  const fontSize = size === "xs" ? 9 : 10;
  return (
    <span
      className="inline-flex items-center gap-1 font-bold uppercase tracking-wider tabular-nums"
      style={{
        backgroundColor: bg,
        color: cfg.fg,
        padding: `${padY}px ${padX}px`,
        fontSize: `${fontSize}px`,
        letterSpacing: "0.08em",
        minWidth: 56,
        justifyContent: "center",
      }}
      data-testid={`heat-chip-${band}`}
      title={days == null ? `${cfg.label} (no activity recorded)` : `${cfg.label} — last activity ${days} day${days === 1 ? "" : "s"} ago`}
    >
      {cfg.label}
      {showDays && days != null && <span className="opacity-75 font-mono">{days}d</span>}
    </span>
  );
}
